<!-- eslint-disable -->

<template>
  <form class="grid" @submit.prevent>
    <fieldset class="col grid" :disabled="false">
      <FormLabel text="Quote Name" class="col-12">
        <InputText
          v-model="advancedSearch.quoteSearch"
          class="quote-name-autocomplete w-full"
          :disabled="false"
          placeholder="Search Quotes"
        />
      </FormLabel>
      <FormLabel text="Proposal Number" class="col-12">
        <InputText
          v-model="advancedSearch.proposalSearch"
          class="quote-name-autocomplete w-full"
          :disabled="false"
        />
      </FormLabel>
      <span class="col-6">
        <router-link :to="{ name: 'QuoteNew' }">
          <Button class="w-full h-full" label="New Quote"></Button>
        </router-link>
      </span>
      <span class="col-6">
        <Button
          class="w-full h-full"
          label="Clear Filters"
          @click="clearFilters"
        ></Button>
      </span>
    </fieldset>
    <fieldset class="col grid" :disabled="false">
      <FormLabel text="Price List" class="col-4">
        <AutoComplete
          v-model="advancedSearch.priceListSearch"
          :loading="fetchingPriceLists"
          class="w-full"
          :suggestions="filteredPriceLists"
          option-value="number"
          option-label="name"
          :dropdown="true"
          dropdownMode="current"
          :disabled="false"
          @complete="completeSearchPriceList"
        />
      </FormLabel>
      <FormLabel ref="nwaLabelElement" text="NWA" class="col-4">
        <AutoComplete
          v-model="advancedSearch.nwaSearch"
          :loading="loadingNwas"
          class="w-full"
          :suggestions="filteredNwas"
          option-value="id"
          :option-label="getNwaOptionLabel"
          :disabled="false"
          :dropdown="true"
          dropdownMode="current"
          @complete="completeSearchNwa"
        />
      </FormLabel>
      <FormLabel text="Account Manager" class="col-4">
        <AutoComplete
          v-model="advancedSearch.userSearch"
          :loading="fetchingUsers"
          class="w-full"
          :suggestions="filteredUsers"
          option-value="display_name"
          option-label="display_name"
          :class="{ 'p-invalid': false }"
          :disabled="false"
          :dropdown="true"
          dropdownMode="current"
          @complete="completeSearchUser"
        />
      </FormLabel>
      <FormLabel text="Well Name / Ship To" class="col-4">
        <AutoComplete
          v-model="advancedSearch.wellAddressSearch"
          :loading="fetchingWellAddresses"
          class="w-full"
          option-label="address.name"
          option-value="address.id"
          :disabled="false"
          :required="true"
          :dropdown="true"
          dropdownMode="current"
          :suggestions="filteredWellAddresses"
          append-to="self"
          @complete="completeSearchWellAddress"
        />
      </FormLabel>
      <FormLabel text="Sales District" class="col-4">
        <!-- Plant Auto complete -->
        <AutoComplete
          v-model="advancedSearch.salesDistrictSearch"
          :loading="fetchingPlants"
          class="w-full"
          option-label="name"
          option-value="name"
          :disabled="false"
          :required="true"
          :suggestions="filteredPlants"
          :dropdown="true"
          dropdownMode="current"
          append-to="self"
          @complete="completeSearchPlant"
        />
      </FormLabel>
      <FormLabel text="Contract" class="col-4">
        <!-- Contract a/c -->
        <AutoComplete
          v-model="advancedSearch.contractSearch"
          :loading="fetchingContracts"
          class="w-full"
          optionValue="number"
          optionLabel="number"
          :disabled="false"
          :suggestions="filteredContracts"
          :dropdown="true"
          dropdownMode="current"
          @complete="completeSearchContract"
        />
      </FormLabel>

      <FormLabel text="Customer" class="col-4">
        <!-- Customer Auto complete -->
        <AutoComplete
          v-model="advancedSearch.customerSearch"
          :loading="fetchingCustomers"
          class="w-full"
          option-label="name"
          option-value="name"
          :disabled="false"
          :required="true"
          :dropdown="true"
          dropdownMode="current"
          :suggestions="filteredCustomers"
          append-to="self"
          @complete="completeSearchCustomers"
        />
      </FormLabel>

      <FormLabel text="Status" class="col-4">
        <!-- Status Auto complete -->
        <AutoComplete
          v-model="advancedSearch.statusSearch"
          class="w-full"
          option-label="label"
          option-value="value"
          :disabled="false"
          :required="true"
          :suggestions="filteredStatuses"
          :dropdown="true"
          append-to="self"
          @complete="completeSearchStatuses"
        />
      </FormLabel>

      <FormLabel text="Pricing Date" class="col-4" @keydown.enter="handleCalendarBlur">
        <Calendar
          ref="calendarRef"
          v-model="advancedSearch.pricingDate"
          date-format="mm/dd/yy"
          class=""
          :manual-input="true"
          show-icon
          show-button-bar
        />
      </FormLabel>
    </fieldset>
  </form>
</template>

<script lang="ts" setup>
/* eslint-disable */

import { ref, computed, toRefs, watch, onMounted } from "vue";
import {  useDebounceFn } from "@vueuse/core";
import Button from "primevue/button";

import FormLabel from "@/components/FormLabel.vue";
import type {
  QuotesListAdvancedSearch,
  WellAddressSearchParams,
  PriceListSearchParams,
  NwaSearchParams,
  PlantSearchParams,
  StatusItem,
  CustomerSearchParams,
  ContractSearchParams
} from "@/components/quotes/types";
import { autoVal, createQueryString, cleanNull, getNwaOptionLabel } from "@/utils/quoteHelpers";

import type {
  SkPriceList,
  SkNwa,
  SkPlant,
  SkContract,
  UsersFieldsFragment,
  SkWell,
  SkWellAddress,
  SkCustomer,
  SkAddress,
} from "gql/api";
import {
  useSkCustomersQuery,
  useSkPriceListsQuery,
  useGetSkNwasQuery,
  useSkPlantsQuery,
  useSkContractsQuery,
  useSkWellAddressesQuery,
  useUsersQuery,
  Quote_Status_Dataset_Enum,
  Users_Select_Column
} from "gql/api";

import InputText from "primevue/inputtext";
import Calendar from "primevue/calendar";
import { LocationQuery } from "vue-router";


const props = defineProps<{
  advancedSearch: QuotesListAdvancedSearch;
  initialRouteQuery: LocationQuery;
}>();
const { advancedSearch, initialRouteQuery } = toRefs(props);

const clearFilters = () => {
  advancedSearch.value.quoteSearch = "";
  advancedSearch.value.proposalSearch = "";
  advancedSearch.value.nwaSearch = "";
  advancedSearch.value.priceListSearch = "";
  advancedSearch.value.contractSearch = "";
  advancedSearch.value.salesDistrictSearch = "";
  advancedSearch.value.userSearch = "";
  advancedSearch.value.wellAddressSearch = "";
  advancedSearch.value.wellSearch= ""; // TODO: revisting this
  advancedSearch.value.addressSearch = ""; // TODO: revisiting this
  advancedSearch.value.customerSearch = "";
  advancedSearch.value.statusSearch = "";
  advancedSearch.value.pricingDate = null;
};

const calendarRef = ref<InstanceType<typeof Calendar>>();

const handleCalendarBlur = () => {
  // Dev note: internal states, such as overlayVisible, are not exposed in the public API
  // of PrimeVue's component. This works, but we have to cast as any to make TS happy.
  const calendar = calendarRef.value as any;
  calendar.$el?.querySelector('input')?.blur(); // blur for better UX
  calendar.overlayVisible = false;
};

// NWA Data Fetching
const filteredNwas = ref<Array<SkNwa>>([]);

let nwaInitFromUrl = false;
watch(filteredNwas, (newNwas) => {
  if(!nwaInitFromUrl && newNwas.length > 0 && initialRouteQuery?.value?.nwa) {
    const initialNwa = newNwas.filter((item) => item.id === initialRouteQuery.value?.nwa)[0];
    if (initialNwa) {
      advancedSearch.value.nwaSearch = initialNwa;
    }
    nwaInitFromUrl = true;
  }
});

const { executeQuery: fetchNwas, fetching: loadingNwas } = useGetSkNwasQuery({
  pause: true,
  requestPolicy: "network-only",
  variables: computed(() => {
    const nwa = (typeof advancedSearch.value?.nwaSearch === "string"
      ? advancedSearch.value?.nwaSearch
      : advancedSearch.value?.nwaSearch?.name || "").trim();

    // ERP matches `name__icontains` against the S4 name and the AL Name. A
    // numeric value (e.g. `?nwa=10090` from the URL) is an NWA id.
    const queryParams: NwaSearchParams = ({
      active: "True",
      deleted: "False",
      expand: "country",
      ordering: "name",
      ...(/^\d+$/.test(nwa) ? { id: nwa } : { name__icontains: nwa })
    });

    return {
      summitQueryString: createQueryString(queryParams)
    };
  })
});

const completeSearchNwa = () => {
  return Promise.resolve()
    .then(() => fetchNwas())
    .then(resp => {
      filteredNwas.value = cleanNull(resp?.data?.value?.getSKNwas || []);
    });
};

// Plant Data Fetching
const filteredPlants = ref<Array<SkPlant>>([]);

let plantInitFromUrl = false;
watch(filteredPlants, (newPlants) => {
  if(!plantInitFromUrl && newPlants.length > 0 && initialRouteQuery.value?.district) {
    const initialPlant = newPlants.filter((item) => item.id === initialRouteQuery.value?.district)[0];
    if (initialPlant) {
      advancedSearch.value.salesDistrictSearch = initialPlant;
    }
    plantInitFromUrl = true;
  }
});

const { executeQuery: fetchPlants, fetching: fetchingPlants } =
  useSkPlantsQuery({
    pause: true,
    requestPolicy: "network-only",
    variables: computed(() => {
      const plantSearch = typeof advancedSearch.value.salesDistrictSearch === "string"
          ? advancedSearch.value.salesDistrictSearch
          : advancedSearch.value.salesDistrictSearch.name || ""

      const queryParams: PlantSearchParams = ({
        is_active: "True",
        group_not_in: "web-service",
        deleted: "False",
        page_size: "all",
        search: plantSearch
      });

      return {
        summitQueryString: createQueryString(queryParams)
      };
    })
  });

const completeSearchPlant = () => {
  return Promise.resolve()
    .then(() => fetchPlants())
    .then(resp => {
      filteredPlants.value = cleanNull(resp?.data?.value?.getSKPlants || []);
    });
};

// Contract Data Fetching
const filteredContracts = ref<Array<SkContract>>([]);

let contractInitFromUrl = false;
watch(filteredContracts, (newContracts) => {
  if(!contractInitFromUrl && newContracts.length > 0 && initialRouteQuery.value?.contract) {
    const initialContract = newContracts.filter((item) => item.number === initialRouteQuery.value?.contract)[0];
    if (initialContract) {
      advancedSearch.value.contractSearch = initialContract;
    }
    contractInitFromUrl = true;
  }
});

const { executeQuery: fetchContracts, fetching: fetchingContracts } =
  useSkContractsQuery({
    pause: true,
    requestPolicy: "network-only",
    variables: computed(() => {
      const contractSearch = typeof advancedSearch.value.contractSearch === "string"
        ? advancedSearch.value.contractSearch
        : advancedSearch.value.contractSearch.number || "";

      const queryParams: ContractSearchParams = ({
        deleted: "False",
        active: "True",
        expand: "price_list",
        ordering: "number",
        page_size: "all",
        number__icontains: contractSearch
      });
      return {
        summitQueryString: createQueryString(queryParams)
      };
    })
  });

const completeSearchContract = () => {
  return Promise.resolve()
    .then(() => fetchContracts())
    .then(resp => {
      // TODO: investigate possible type narrowing with SkContracts. In general there's possible
      // inconsistencies and/or weird behavior with how the backend returns contracts, need to investigate
      filteredContracts.value = cleanNull(resp?.data?.value?.getSKContracts || []);
    });
};

// Well Address Data Fetching
const filteredWellAddresses = ref<Array<SkWellAddress>>([]);

let wellAddressInitFromUrl = false;
watch(filteredWellAddresses, (newWellAddresses) => {
  if(!wellAddressInitFromUrl && newWellAddresses.length > 0 && initialRouteQuery.value?.wellAddress) {
    const initialWellAddress = newWellAddresses.filter((wellAddress) => wellAddress?.address?.id === initialRouteQuery.value?.wellAddress)[0];
    if (initialWellAddress) {
      advancedSearch.value.wellAddressSearch = initialWellAddress;
    }
    wellAddressInitFromUrl = true;
  }
});

const { executeQuery: fetchWellAddresses, fetching: fetchingWellAddresses } =
  useSkWellAddressesQuery({
    pause: true,
    requestPolicy: "network-only",
    variables: computed(() => {

      const wellAddressSearch = typeof advancedSearch.value.wellAddressSearch === "string"
        ? advancedSearch.value.wellAddressSearch
        : advancedSearch.value.wellAddressSearch.address?.id
          ? String(advancedSearch.value.wellAddressSearch.address?.id)
          : ""; // <--- Will prevent "undefined" as a string from being sent to the backend

      const queryParams: WellAddressSearchParams = ({
        deleted: "False",
        ordering: "name",
        expand: "well,address.region.country,customer",
        include_null_addresses: "False",
        search: wellAddressSearch
      });

      return {
        summitQueryString: createQueryString(queryParams)
      };
    })
  });

const completeSearchWellAddress = () => {
  return Promise.resolve()
    .then(() => fetchWellAddresses())
    .then(resp => {
      filteredWellAddresses.value = cleanNull(resp?.data?.value?.getSKWellAddresses || []);
    });
};

// Customer Data Fetching
const filteredCustomers = ref<Array<SkCustomer>>([]);

let customersInitFromUrl = false;
watch(filteredCustomers, (newCustomers) => {
  if(!customersInitFromUrl && newCustomers.length > 0 && initialRouteQuery.value?.customer) {
    const initialCustomer = newCustomers.filter((item) => item.id === initialRouteQuery.value?.customer)[0];
    if (initialCustomer) {
      advancedSearch.value.customerSearch = initialCustomer;
    }
    customersInitFromUrl = true;
  }
});

const { executeQuery: fetchCustomers, fetching: fetchingCustomers } =
  useSkCustomersQuery({
    pause: true,
    requestPolicy: "network-only",
    variables: computed(() => {
      const customerName =
        typeof advancedSearch.value.customerSearch === "string"
          ? advancedSearch.value.customerSearch
          : advancedSearch.value.customerSearch.name || "";

      const queryParams: CustomerSearchParams = ({
        deleted: "False",
        search: customerName
      });

      return {
        summitQueryString: createQueryString(queryParams)
      };
    })
  });

const completeSearchCustomers = () => {
  Promise.resolve()
    .then(() => fetchCustomers())
    .then(resp => {
      filteredCustomers.value = cleanNull(resp?.data?.value?.getSKCustomers || []);
    });
};


const filteredStatuses = ref<Array<StatusItem>>([]);
const completeSearchStatuses = () => {
  // TODO: candidate for revisiting separation of concerns here.
  // Doesn't feel right to have some logic in the function.
  // It's a combination of fetching and searching patterns found in other inputs.
  // I know this is potential maintenance problem, coming back to it later.

  const statusSearch =
    typeof advancedSearch.value.statusSearch === "string"
      ? advancedSearch.value.statusSearch
      : advancedSearch.value.statusSearch.value;

  filteredStatuses.value = Object.values(Quote_Status_Dataset_Enum).
    map((value) => ({
      value,
      label: value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
    })
  ).filter((status) => status.value.includes(statusSearch));
};

// Price List Data Fetching
const filteredPriceLists = ref<Array<SkPriceList>>([]);

let priceListInitFromUrl = false;
watch(filteredPriceLists, (newPriceLists) => {
  if(!priceListInitFromUrl && newPriceLists.length > 0 && initialRouteQuery.value?.priceList) {
    const initialPriceList = newPriceLists.filter((item) => item.number === initialRouteQuery.value?.priceList)[0];
    if (initialPriceList) {
      advancedSearch.value.priceListSearch = initialPriceList;
    }
    priceListInitFromUrl = true;
  }
});

const { executeQuery: fetchPriceLists, fetching: fetchingPriceLists } =
  useSkPriceListsQuery({
    pause: true,
    requestPolicy: "network-only",
    variables: computed(() => {
      const priceListSearch =
        typeof advancedSearch.value.priceListSearch === "string"
          ? advancedSearch.value.priceListSearch
          : advancedSearch.value.priceListSearch.name || "";

      const queryParams: PriceListSearchParams = ({
        active: "True",
        deleted: "False",
        ordering: "name",
        name__icontains: priceListSearch
      });

      return {
        summitQueryString: createQueryString(queryParams)
      };
    })
  });

const completeSearchPriceList = () => {
  return Promise.resolve()
    .then(() => fetchPriceLists())
    .then(resp => {
      filteredPriceLists.value = cleanNull(resp?.data?.value?.getSKPriceLists || []);
    });
};

// User Data Fetching
const filteredUsers = ref<Array<UsersFieldsFragment>>([]);

let userInitFromUrl = false;
watch(filteredUsers, (newUsers) => {
  if(!userInitFromUrl && newUsers.length > 0 && initialRouteQuery.value?.user) {
    const initialUser = newUsers.filter((item) => item.id === initialRouteQuery.value?.user)[0];
    if (initialUser) {
      advancedSearch.value.userSearch = initialUser;
    }
    userInitFromUrl = true;
  }
});

const { executeQuery: fetchUsers, fetching: fetchingUsers } = useUsersQuery({
  pause: true,
  requestPolicy: "network-only",
  variables: computed(() => {
    return {
      limit: 20,
      offset: 0,
      distinct_on: [Users_Select_Column.DisplayName], // Edge case: duplicate users. Just pick any dupe user. Their HAL IDs are identical.
      where: {
        display_name: {
          _ilike: `%${autoVal(advancedSearch.value.userSearch, Users_Select_Column.DisplayName)}%`
        },
        hal_identifier: {
          _is_null: false // <--- filters out dupe users with null HAL IDs (SK needs HAL ID for job create)
        }
      }
    };
  })
});

const completeSearchUser = () => {
  return Promise.resolve()
    .then(() => fetchUsers())
    .then(resp => {
      filteredUsers.value = cleanNull(resp?.data?.value?.users || []);
    });
};

onMounted(() => {
  /** Execute *applicable* queries once on page load to restore
   * needed information missing from volatile advancedSearch object
   * TODO: onMounted may not be explicitly required here */

  // Dev note: uncomment when bringing back the following:
  // initialRouteQuery?.value?.address ? completeSearchWellAddress() : null;
  // initialRouteQuery?.value?.well ? completeSearchWell() : null;
  initialRouteQuery?.value?.wellAddress ? completeSearchWellAddress() : null;
  initialRouteQuery?.value?.user ? completeSearchUser() : null;
  initialRouteQuery?.value?.customer ? completeSearchCustomers() : null;
})
</script>

<style lang="scss" scoped>
.calendar-date-range {
  height: 83px;
}

:deep {
  // TODO: implement re-usable pattern in EditQuote.vue
  .fixed-inner-label {
    white-space: nowrap; // prevents label wrapping if too long
    text-indent: 0;
    user-select: none; // fixes outside of label from being selectable/highlightable

    label {
      // Fixed inner label behavior
      user-select: none;
      pointer-events: none;
    }
  }
}
</style>
